import { CircleTopicBaseFragment } from '@modernhealth/circles-graphql/v2';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntl } from 'react-intl';

import { useFetchUpcomingTopicsQuery } from '../documents/topics.generated';

import { CircleOnDemandPageSection } from './CircleOnDemandPageSection';
import { CirclePageSection } from './CirclePageSection';
import { CircleTopicCard } from './CircleTopicCard';
import { SessionCardRowSkeleton } from './SessionCardSkeletons';

export const UpcomingByLanguageRowSkeleton = ({ header }: { header: string }) => {
    return (
        <CirclePageSection header={header}>
            <SessionCardRowSkeleton />
        </CirclePageSection>
    );
};

export function useTranslatedHeader(languageCode: string) {
    const { t } = useTranslation('circles');
    const intl = useIntl();
    const language = useMemo(() => {
        return intl.formatDisplayName(languageCode, {
            type: 'language',
        });
    }, [intl, languageCode]);

    return t('pages.on_demand.sections.language.title', { language });
}

export const CirclesUpcomingTopicsByLanguage: React.FC<{
    languageCode: string;
}> = ({ languageCode }) => {
    const { data, loading } = useFetchUpcomingTopicsQuery({
        variables: {
            filterByLanguageCodes: [languageCode],
        },
    });
    const { t } = useTranslation('circles');
    const header = t('pages.on_demand.sections.upcoming_circles.title');

    if (loading) {
        return <UpcomingByLanguageRowSkeleton header={header} />;
    }

    if (data?.circleTopicConnection.__typename !== 'CircleTopicConnection') {
        return null;
    }

    const circles = data?.circleTopicConnection.edges;
    // If no circles, we're not going to render anything here since it is a section on the screen,
    // we can update later if we decide later to say a specific not in this case
    if (!circles || !circles.length) return null;

    return (
        <CircleOnDemandPageSection header={header}>
            {circles.map((item, i) => {
                const topic = item?.node;
                if (!topic) return null;
                return (
                    <CircleTopicCard
                        key={topic.id}
                        topic={topic as CircleTopicBaseFragment}
                        id={`upcoming-topic-${topic.id}-${i}`}
                    />
                );
            })}
        </CircleOnDemandPageSection>
    );
};
